// pages/Imports.tsx — /app/imports (Settings → Imports; Task O, Alto import). Bringing records over from Alto or any CSV:
// the jobs so far, a new import (kind → preset → file → mapping → dry run → run, continued on /app/imports/:id) and the
// reconciliation of a company's ledger against the trial balance imported for it.
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageHeader, Card, Badge, EmptyState, Tabs, SkeletonRows } from '../components/ui';

import { NewImport } from '../components/imports/NewImport';
import { Reconciliation } from '../components/imports/Reconciliation';
import { importsApi, KIND_LABEL, STATUS_LABEL, type ImportStatus } from '../lib/imports';

const TONE: Record<ImportStatus, 'good' | 'neutral' | 'warning' | 'critical'> = { UPLOADED: 'neutral', VALIDATED: 'warning', DRY_RUN_OK: 'neutral', IMPORTING: 'warning', DONE: 'good', FAILED: 'critical' };

export default function ImportsPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'reconciliation' ? 'reconciliation' : 'jobs';
  const [creating, setCreating] = useState(params.get('new') === '1');
  const set = (k: string, v: string) => setParams((p) => { const n = new URLSearchParams(p); if (v) n.set(k, v); else n.delete(k); return n; }, { replace: true });
  const jobs = useQuery({ queryKey: ['imports'], queryFn: () => importsApi.list() });

  return (
    <div className="space-y-2.5">
      <PageHeader eyebrow="Settings" title="Imports"
        menu={[{ label: 'New import', icon: 'Plus', can: 'imports.create', onClick: () => { setCreating(true); set('tab', ''); } }]} />
      <Tabs items={[{ key: 'jobs', label: 'Imports' }, { key: 'reconciliation', label: 'Reconciliation' }]} value={tab} onChange={(t) => set('tab', t === 'jobs' ? '' : t)} />

      {tab === 'reconciliation' ? <Reconciliation entityId={params.get('entityId') ?? ''} onEntity={(id) => set('entityId', id)} /> : (
        <div className="space-y-2.5">
          {creating && <NewImport onCancel={() => setCreating(false)} />}
          <Card title="Imports so far">
            {jobs.isLoading ? <SkeletonRows rows={3} cols={3} /> : !jobs.data?.length ? (
              <div><EmptyState icon="Upload" title="Nothing imported yet" body="Start with Alto's property export: it creates the buildings and the flats." /></div>
            ) : (
              <ul className="-m-4 divide-y divide-ink-100">
                {jobs.data.map((j) => (
                  <li key={j.id}>
                    <Link to={`/app/imports/${j.id}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 hover:bg-ink-50/60">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[13px] font-semibold text-ink-900">{j.fileName}</div>
                        <div className="text-[11.5px] text-ink-500">{KIND_LABEL[j.kind]} · {j.source === 'ALTO' ? 'Alto' : 'CSV'}{j.entityName ? ` · ${j.entityName}` : ''}{j.asOf ? ` · as at ${j.asOf}` : ''} · {new Date(j.createdAt).toLocaleDateString('en-GB')}{j.createdByName ? ` by ${j.createdByName}` : ''}</div>
                      </div>
                      <div className="text-[11.5px] tabular-nums text-ink-500">{j.counts.rows} rows{j.status === 'DONE' ? ` · ${j.counts.created} new · ${j.counts.updated} updated` : ''}{j.counts.errors ? <span className="text-red-700"> · {j.counts.errors} errors</span> : null}</div>
                      <Badge value={STATUS_LABEL[j.status]} tone={TONE[j.status]} />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
